import { Box, Card, CardContent, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import moment from "moment";
import { FacturaInforme, PresetFechas } from "../Informes";
import { formatCurrency } from "../../../../utils/Factura";

interface GraficoIngresosTiempoProps {
  facturas: FacturaInforme[];
  presetFechas: PresetFechas;
}

type Agrupacion = "dia" | "semana" | "mes";
type TipoFactura = "cita" | "bono" | "clase" | "agrupada" | "manual";
const tiposFactura: TipoFactura[] = ["cita", "bono", "clase", "agrupada", "manual"];
const colorMap: Record<TipoFactura, string> = {
  cita: "#0B3D20",
  bono: "#1B5E20",
  clase: "#2E7D32",
  agrupada: "#66BB6A",
  manual: "#2C3E50",
};
const labelMap: Record<TipoFactura, string> = {
  cita: "Citas",
  bono: "Bonos",
  clase: "Clases",
  agrupada: "Complementarias",
  manual: "Manuales",
};

const getAgrupacion = (
  preset: PresetFechas,
  facturas: FacturaInforme[]
): Agrupacion => {
  if (preset !== "Personalizado") {
    return (preset === "Hoy" || preset === "Este mes") ? "dia" : "mes";
  }

  const fechas = facturas.filter(f => f.fechaPago).map(f => moment(f.fechaPago));
  if (fechas.length === 0) return "dia";

  const min = moment.min(fechas);
  const max = moment.max(fechas);
  const diffDias = max.diff(min, "days");

  return diffDias <= 31 ? "dia" : "mes"
};

const getKeyAndLabel = (date: Date, agrupacion: Agrupacion) => {
  const m = moment(date);

  if (agrupacion === "dia") {
    return {
      key: m.format("YYYY-MM-DD"),
      label: m.format("DD/MM/YYYY"),
      tooltipLabel: m.format("D MMMM YYYY")
    };
  }
  return {
    key: m.format("YYYY-MM"),
    label: m.format("MMMM YYYY"),
    tooltipLabel: m.format("MMMM YYYY")
  };
};

const CustomTooltip = ({ active, payload, moneda }: any) => {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "grey.300",
        p: 1,
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">{data.tooltipLabel.charAt(0).toUpperCase() + data.tooltipLabel.slice(1)}</Typography>
      {tiposFactura.map(tipo => {
        const value = data[tipo];
        if (!value) return null;
        return (
          <Typography key={tipo} variant="body2">
            {labelMap[tipo]}: {formatCurrency(value, moneda)}
          </Typography>
        );
      })}
      <Typography variant="h6" fontWeight={600}>
        Total: {formatCurrency(tiposFactura.reduce((sum, t) => sum + (data[t] ?? 0), 0), moneda)}
      </Typography>
    </Box>
  );
};

type DataItem = { label: string; tooltipLabel: string; } & Record<TipoFactura, number>;

const GraficoIngresosTiempoChart = ({
  facturas,
  agrupacion,
  moneda,
  titulo,
}: {
  facturas: FacturaInforme[];
  agrupacion: Agrupacion;
  moneda: string;
  titulo: string;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dataMap = facturas.reduce((acc, f) => {
    if (!f.fechaPago || f.estadoPago !== "Pagada") return acc;

    const { key, label, tooltipLabel } = getKeyAndLabel(
      new Date(f.fechaPago),
      agrupacion
    );

    if (!acc[key]) {
      acc[key] = {
        label,
        tooltipLabel,
        cita: 0,
        bono: 0,
        clase: 0,
        agrupada: 0,
        manual: 0,
      };
    }

    const tipo = f.tipo.toLowerCase() as TipoFactura;

    if (tiposFactura.includes(tipo)) {
      acc[key][tipo] += f.total;
    }

    return acc;
  }, {} as Record<string, DataItem>);

  const data = Object.keys(dataMap)
  .sort()
  .map(key => {
    const item = dataMap[key];
    const ingresos = tiposFactura.reduce(
      (sum, t) => sum + item[t],
      0
    );
    return {
      fecha: key,
      ingresos,
      ...item,
    };
  });

  const generarTicks = (max: number, numTicks: number = 5) => {
    if (max === 0) return [0];
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    const roundedMax = Math.ceil(max / magnitude) * magnitude;
    const step = roundedMax / numTicks;
    return Array.from({ length: numTicks + 1 }, (_, i) => Math.round(i * step));
  };

  const maxIngresos = Math.max(...data.map(d => d.ingresos), 0);
  const yTicks = generarTicks(maxIngresos); 
  const legendTipos = tiposFactura.filter(t => data.some(d => d[t] > 0));

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {titulo}
        </Typography>

        <Box>
          <Box height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 30, right: 20, left: 0, bottom: 30 }}
              >
                {!isMobile && <>
                <XAxis
                  dataKey="label"
                  interval={0}
                  angle={agrupacion === "mes" ? 0 : -45}
                  textAnchor={agrupacion === "mes" ? "middle" : "end"}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  ticks={yTicks}
                  tickFormatter={v => formatCurrency(v, moneda)}
                  tick={{ fontSize: 12 }}
                />
                </>}
                <Tooltip content={<CustomTooltip moneda={moneda} />} />

                {legendTipos.map(tipo => (
                  <Bar
                    key={tipo}
                    dataKey={tipo}
                    name={labelMap[tipo]}
                    stackId="a"
                    fill={colorMap[tipo]}
                    isAnimationActive
                    animationDuration={400}
                    animationEasing="ease-out"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
              
          {!isMobile && <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
            {legendTipos.map(tipo => (
              <Box key={tipo} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 14, height: 12, backgroundColor: colorMap[tipo] }} />
                <Typography variant="body2">{labelMap[tipo]}</Typography>
              </Box>
            ))}
          </Box>}
        </Box>
      </CardContent>
    </Card>
  );
};

const GraficoIngresosTiempo = ({
  facturas,
  presetFechas,
}: GraficoIngresosTiempoProps) => {
  const agrupacion = getAgrupacion(presetFechas, facturas);
  const moneda = facturas[0]?.moneda || 'EUR';

  return (
    <GraficoIngresosTiempoChart
      facturas={facturas}
      agrupacion={agrupacion}
      moneda={moneda}
      titulo="Ingresos en el tiempo"
    />
  );
};

export default GraficoIngresosTiempo;